/**
 * Landing Page Features Section
 * ZO Bull Algo Trading Platform
 * 
 * Features are loaded from CMS API (/api/cms/landing/features)
 * If API fails, falls back to default features.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Feature {
  icon: string;
  title: string;
  description: string;
  color: string;
  is_new?: boolean;
  is_beta?: boolean;
}

const DEFAULT_FEATURES: Feature[] = [
  { icon: '🤖', title: 'featureAiCopilot', description: 'descAiCopilot', color: '#667eea' },
  { icon: '📊', title: 'featureStrategyBuilder', description: 'descStrategyBuilder', color: '#10b981' },
  { icon: '🔄', title: 'featureAutoTrading', description: 'descAutoTrading', color: '#f59e0b' },
  { icon: '📈', title: 'featureBacktesting', description: 'descBacktesting', color: '#3b82f6' },
  { icon: '💰', title: 'featureMarketplace', description: 'descMarketplace', color: '#ec4899' },
  { icon: '🔔', title: 'featureSmartAlerts', description: 'descSmartAlerts', color: '#8b5cf6' },
  { icon: '📊', title: 'featureOptionsTrading', description: 'descOptionsTrading', color: '#3b82f6' },
  { icon: '📝', title: 'featurePineScript', description: 'descPineScript', color: '#22c55e' },
  { icon: '🔗', title: 'featureTradetrON', description: 'descTradetrON', color: '#f97316' },
  { icon: '🌍', title: 'featureMultiExchange', description: 'descMultiExchange', color: '#06b6d4' },
  { icon: '🎨', title: 'featureThemes', description: 'descThemes', color: '#f43f5e' },
  { icon: '📱', title: 'featureMobileTrading', description: 'descMobileTrading', color: '#22c55e' },
];

export default function LandingFeatures() {
  const { t } = useTranslation();
  const [features, setFeatures] = useState<Feature[]>(DEFAULT_FEATURES);
  const [useTranslationKeys, setUseTranslationKeys] = useState(true);

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await fetch('/api/cms/landing/features');
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          // CMS returns translated text directly
          setFeatures(data.map((f: any) => ({
            ...f,
            title: f.title,
            description: f.description
          })));
          setUseTranslationKeys(false);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch CMS features, using defaults');
    }
  };

  const getFeatureTitle = (key: string) => {
    return useTranslationKeys ? t(`landing.${key}`) : key;
  };

  const getFeatureDesc = (key: string) => {
    return useTranslationKeys ? t(`landing.${key}`) : key;
  };

  return (
    <section className="landing-features">
      <div className="section-container">
        <div className="section-header">
          <span className="section-badge">✨ {t('landing.features')}</span>
          <h2 className="section-title">{t('landing.featuresTitle')}</h2>
          <p className="section-subtitle">
            {t('landing.featuresSubtitle')}
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className="feature-card"
              style={{ '--feature-color': feature.color } as React.CSSProperties}
            >
              <div className="feature-icon-wrapper">
                <span className="feature-icon">{feature.icon}</span>
                {feature.is_new && <span className="feature-badge new">NEW</span>}
                {feature.is_beta && <span className="feature-badge beta">BETA</span>}
              </div>
              <h3 className="feature-title">{getFeatureTitle(feature.title)}</h3>
              <p className="feature-description">{getFeatureDesc(feature.description)}</p>
              <div className="feature-glow"></div>
            </div>
          ))}
        </div>

        <div className="features-cta">
          <Link to="/login" className="btn btn-primary btn-lg">
            {t('landing.getStartedFree')}
          </Link>
          <span className="cta-note">{t('landing.noCreditCardRequired')}</span>
        </div>
      </div>
    </section>
  );
}
